/**
 * SegmentBroadCastForm コンポーネント
 *
 * セグメント配信用のフォームコンポーネント。
 * セグメント条件設定、送信店舗選択設定、配信設定機能を提供。
 * Cloud Functionでの処理に対応したセグメント条件の設定が可能。
 *
 * @file セグメント配信フォーム
 * @date 2025/09/18
 */

import React, { useState, useCallback } from 'react';
import {
  Form,
  Input,
  Button,
  Space,
  DatePicker,
  Select,
  Card,
  Divider,
  InputNumber,
  Checkbox,
  Typography,
  Tag,
  Row,
  Col,
  message,
  Radio,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DownloadButton } from 'src/components/ui';
import { download } from 'src/util/download';
import { FullscreenLoading } from 'src/components/function/loading';
import { useSegmentResultRepository } from 'src/repositories/segmentResult/repository';
import { BroadCastFormType } from 'src/models/broadCast';
import { useLoginAccount } from 'src/globalStates/loginAccount';
import TextArea from 'antd/es/input/TextArea';
import { useBroadCastForm } from 'src/usecases/broadCast/form';
import { usePointCardList } from 'src/usecases/pointCard/reader';
import { useSegmentResult } from 'src/usecases/segmentResult/reader';
import { useSurveyList } from 'src/usecases/survey/reader';
import dayjs, { Dayjs } from 'dayjs';
import { MessageTemplate } from 'src/models/messageTemplate';
import { ImageUpload } from './shared/ImageUpload';
import { TemplateSelect } from './shared/TemplateSelect';
import { useImageHandling } from './shared/useImageHandling';

const { RangePicker } = DatePicker;
const { Title } = Typography;

/**
 * Props インターフェース
 */
interface Props {
  /** 編集時の初期値 */
  init?: BroadCastFormType;
  /** 配信タイプ切り替え時のリセットフラグ */
  deliveryTypeChanged?: boolean;
}

/**
 * セグメント条件のキータイプ
 */
type ConditionKey = 'specificDateVisit' | 'visitPattern' | 'customerRank' | 'itemPreferences' | 'pointCard' | 'surveyAnswers';

/**
 * Firestoreから取得したセグメント条件の日付をdayjsに変換
 */
const convertSegmentConditionsDates = (segmentConditions: any) => {
  if (!segmentConditions) return segmentConditions;

  const converted = { ...segmentConditions };

  // 特定日来店の日付変換
  if (converted.specificDateVisit?.targetDates) {
    converted.specificDateVisit.targetDates = converted.specificDateVisit.targetDates.map((date: any) => {
      // Timestampオブジェクトの場合
      if (date && typeof date.toDate === 'function') {
        return dayjs(date.toDate());
      }
      // Dateオブジェクトの場合
      if (date instanceof Date) {
        return dayjs(date);
      }
      // すでにdayjsオブジェクトの場合はそのまま
      return date;
    });
  }

  // 来店パターンの期間変換
  if (converted.visitPattern?.dateRange) {
    converted.visitPattern.dateRange = converted.visitPattern.dateRange.map((date: any) => {
      // Timestampオブジェクトの場合
      if (date && typeof date.toDate === 'function') {
        return dayjs(date.toDate());
      }
      // Dateオブジェクトの場合
      if (date instanceof Date) {
        return dayjs(date);
      }
      // すでにdayjsオブジェクトの場合はそのまま
      return date;
    });
  }

  return converted;
};


/**
 * SegmentBroadCastForm コンポーネント
 *
 * セグメント配信用のフォームコンポーネント。
 * 条件に基づく顧客セグメント設定とCloud Function連携による配信を提供。
 *
 * @param props - コンポーネントのプロパティ
 * @returns JSX.Element
 */
export const SegmentBroadCastForm: React.FC<Props> = ({ init, deliveryTypeChanged }) => {
  // Hooks
  const loginAccount = useLoginAccount();
  const _formState = useBroadCastForm();

  // ポイントカード一覧取得を追加
  const { data: pointCardData, isLoading: pointCardLoading } = usePointCardList();

  // アンケート一覧取得を追加
  const { data: surveyData, isLoading: surveyLoading } = useSurveyList();

  // セグメント検索結果リポジトリ
  const segmentResultRepo = useSegmentResultRepository();

  // セグメント結果取得
  const { data: segmentResultData } = useSegmentResult({
    account: loginAccount,
    broadCastId: init?.broadCast?.id || '',
  });

  // フォーム関連
  const [form] = Form.useForm<BroadCastFormType>();
  const type = Form.useWatch('type', form);

  // 状態管理
  const [loading, setLoading] = React.useState(false);
  const [searchLoading, setSearchLoading] = React.useState(false);

  // セグメント条件表示状態管理
  const [activeConditions, setActiveConditions] = useState<ConditionKey[]>(() => {
    // 編集時: initからactiveな条件を抽出
    if (init?.broadCast?.segmentConditions) {
      const conditions: ConditionKey[] = [];
      const segmentConditions = init.broadCast.segmentConditions;
      
      if (segmentConditions.specificDateVisit) conditions.push('specificDateVisit');
      if (segmentConditions.visitPattern) conditions.push('visitPattern');
      if (segmentConditions.customerRank) conditions.push('customerRank');
      if (segmentConditions.itemPreferences) conditions.push('itemPreferences');
      if (segmentConditions.pointCard) conditions.push('pointCard');
      if (segmentConditions.surveyAnswers) conditions.push('surveyAnswers');
      
      return conditions;
    }
    // 新規作成時: 空配列
    return [];
  });

  // ダウンロード ローディング表示
  const [downloadLoading, setDownloadLoading] = useState(false);

  // initialValuesを変換
  const initialValues = React.useMemo(() => {
    if (!init) return init;
    
    return {
      ...init,
      broadCast: init.broadCast ? {
        ...init.broadCast,
        segmentConditions: convertSegmentConditionsDates(init.broadCast.segmentConditions)
      } : undefined
    };
  }, [init]);


  // アンケート回答条件用の状態
  const surveyAnswers = Form.useWatch(['segmentConditions', 'surveyAnswers'], form) || [];

  // 送信内容タイプに応じた表示制御
  const _needText = type === 'text' || type === 'textAndImage' || type === 'textAndTemplate';
  const _needImage = type === 'image' || type === 'textAndImage';
  const _needTemplate = type === 'template' || type === 'textAndTemplate';

  /**
   * 利用可能なセグメント条件
   */
  const availableConditions: { key: ConditionKey; label: string; color: string }[] = [
    { key: 'specificDateVisit', label: '特定日来店', color: 'volcano' },
    { key: 'visitPattern', label: '来店パターン', color: 'blue' },
    { key: 'customerRank', label: '顧客ランク', color: 'green' },
    // TODO: グランドメニューの商品ID統一が未実装のため一旦非表示
    // { key: 'itemPreferences', label: '商品購入パターン', color: 'orange' },
    { key: 'pointCard', label: 'ポイントカード', color: 'purple' },
    { key: 'surveyAnswers', label: 'アンケート回答', color: 'cyan' },
  ];

  // 共通フックを使用
  const {
    inputImage,
    templateImageUrl,
    getExistingImageUrl,
    handleImageChange,
    handleTemplateImageClear,
    setInputImage,
    setTemplateImageUrl,
  } = useImageHandling({ init, type });

  /**
   * セグメント条件の追加
   */
  const addCondition = (conditionKey: ConditionKey) => {
    if (!activeConditions.includes(conditionKey)) {
      setActiveConditions([...activeConditions, conditionKey]);
      
      // 来店パターンの来店回数、条件：以上のデフォルト値を設定
      if (conditionKey === 'visitPattern') {
        form.setFieldsValue({
          segmentConditions: {
            ...form.getFieldValue('segmentConditions'),
            visitPattern: {
              visitCount: {
                count: 1,
                operator: 'above'
              }
            }
          }
        });
      }

      // ポイントカードのポイント範囲のデフォルト値を設定
      if (conditionKey === 'pointCard') {
        form.setFieldsValue({
          segmentConditions: {
            ...form.getFieldValue('segmentConditions'),
            pointCard: {
              pointRange: {
                min: 0,
                max: 999999
              }
            }
          }
        });
      }
      
    }
  };

  /**
   * セグメント条件の削除
   */
  const removeCondition = (conditionKey: ConditionKey) => {
    setActiveConditions(activeConditions.filter(key => key !== conditionKey));
    // フォームの該当フィールドもクリア
    form.setFieldValue(['segmentConditions', conditionKey], undefined);
    // 対象者数をクリア（条件変更時）
  };

  /**
   * 日付範囲のプリセット設定
   * 通常配信と同じプリセットオプションを提供
   *
   * @param preset - プリセット種類
   * @returns 日付範囲の配列
   */
  const getPresetDateRange = (preset: string): [Dayjs, Dayjs] => {
    switch (preset) {
      // 今週 (例)2024/10/30日の場合 → 2024/10/28 ～ 2024/10/30
      case 'thisWeek':
        return [
          dayjs().startOf('week'),
          dayjs()
        ];
      // 先週 (例)2024/10/30日の場合 → 2024/10/21 ～ 2024/10/27
      case 'lastWeek':
        return [
          dayjs().subtract(1, 'week').startOf('week'),
          dayjs().subtract(1, 'week').endOf('week')
        ];
      // 今月 (例)2024/10/30日の場合 → 2024/10/01 ～ 2024/10/30
      case 'thisMonth':
        return [
          dayjs().startOf('month'),
          dayjs()
        ];
      // 先月 (例)2024/10/30日の場合 → 2024/09/01 ～ 2024/09/30
      case 'lastMonth':
        return [
          dayjs().subtract(1, 'month').startOf('month'),
          dayjs().subtract(1, 'month').endOf('month')
        ];
      // 半年前までの範囲 (例)2024/10/30日の場合 → 2024/05/01 ～ 2024/10/30
      case 'halfYear':
        return [
          dayjs().subtract(6, 'month').startOf('month'),
          dayjs()
        ];
      // 今年の範囲 (例)2024/10/30日の場合 → 2024/01/01 ～ 2024/10/30
      case 'thisYear':
        return [
          dayjs().startOf('year'),
          dayjs()
        ];
      // 去年の範囲 (例)2024/10/30日の場合 → 2023/01/01 ～ 2023/12/31
      case 'lastYear':
        return [
          dayjs().subtract(1, 'year').startOf('year'),
          dayjs().subtract(1, 'year').endOf('year')
        ];
      default:
        return [dayjs(), dayjs()];
    }
  };

  /**
   * テンプレート選択時の処理
   * 選択されたテンプレートの内容をフォームに反映し、画像の状態を更新する
   *
   * @param template - 選択されたメッセージテンプレート
   */
  const handleSelectTemplate = (template: MessageTemplate) => {
    form.setFieldsValue({
      title: template.title || '',
      type: template.broadCastMessage?.type || 'text',
      text: template.broadCastMessage?.text || '',
      altText: template.broadCastMessage?.altText || '',
      tempLabel: template.broadCastMessage?.tempLabel || '',
      tempUri: template.broadCastMessage?.tempUri || '',
    });

    setInputImage(undefined);

    const imageUrl = template.broadCastMessage?.imageUrl || template.broadCastMessage?.tempImageUrl;
    if (imageUrl) {
      setTemplateImageUrl(imageUrl);
    } else {
      setTemplateImageUrl(null);
    }
  };

  /**
   * セグメント検索実行処理
   * 設定された条件に基づいて配信対象者を検索し、結果を保存
   */
  const onSegmentSearch = async (): Promise<void> => {
    // バックエンドで検索中の場合は実行しない
    if (init?.broadCast?.segmentJobStatus === 'processing') {
      message.warning('検索処理が既に実行中です。結果が出るまでお待ちください。');
      return;
    }

    setSearchLoading(true);
    try {
      const values = form.getFieldsValue();

      await _formState.submitSegment(
        {
          ...values,
          broadCast: init?.broadCast,
          segmentConditions: values.segmentConditions,
          storeSelectionStrategy: values.storeSelectionStrategy,
        },
        'draft',
        inputImage,
        templateImageUrl,
        true
      );

      setSearchLoading(false);
      
    } 
    catch (error) {
      setSearchLoading(false);
    }
  };

  /**
   * 下書き保存処理（検索前でも可能）
   */
  const onSaveDraft = async (): Promise<void> => {
    setLoading(true);
    try {
      const values = form.getFieldsValue();

      await _formState.submitSegment(
        {
          ...values,
          broadCast: init?.broadCast,
          deliveryType: 'segment' as const,
        },
        'draft',
        inputImage,
        templateImageUrl,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * 配信確定処理（検索結果がある場合のみ）
   */
  const onConfirmDelivery = async (): Promise<void> => {
    setLoading(true);
    try {
      const values = form.getFieldsValue();

      await _formState.submitSegment(
        {
          ...values,
          broadCast: init?.broadCast,
        },
        'reserved',
        inputImage,
        templateImageUrl,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * セグメント検索結果プレビューコンポーネント
   */
  const SegmentResultPreview = () => {
    if (!segmentResultData) {
      return null;
    }

    // エラー状態の表示
    if (segmentResultData.status === 'error') {
      return (
        <Card size="small" style={{ marginBottom: 16 }} title="検索エラー">
          <div style={{ padding: 16, backgroundColor: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 6 }}>
            <strong style={{ color: '#d4380d' }}>検索処理でエラーが発生しました</strong>
            {segmentResultData.error && (
              <div style={{ marginTop: 8, color: '#666' }}>
                {segmentResultData.error}
              </div>
            )}
          </div>
        </Card>
      );
    }

    // 成功時の表示
    return (
      <Card size="small" style={{ marginBottom: 16 }} title="配信対象者情報">
        {/* 配信対象者数表示 */}
        <div style={{ padding: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, marginBottom: 16 }}>
          <strong style={{ fontSize: 18, color: '#52c41a' }}>
            {(segmentResultData.customerCount ?? 0).toLocaleString()}人
          </strong>
          <span style={{ marginLeft: 8, color: '#666' }}>
            が配信対象として検索されました
          </span>
        </div>

        {/* ダウンロードボタン */}
        {segmentResultData.customerCount && segmentResultData.customerCount > 0 ? (
          <div style={{ textAlign: 'center' }}>
            <DownloadButton
              name="配信対象者リスト"
              tooltip="配信対象者のLINE IDと配信店舗IDをダウンロード"
              onClick={handleDownloadSegmentResult}
              size="small"
            />
          </div>
        ) : null}
      </Card>
    );
  };

  /**
   * 店舗選択コンポーネント
   * 全選択・全解除ボタン付き
   */
  const StoreSelect: React.FC<{
    value?: string[];
    onChange?: (value: string[]) => void;
  }> = ({ value, onChange }) => {
    return (
      <div>
        <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
          <Button
            type="link"
            size="small"
            onClick={() => {
              const allStoreIds = loginAccount.stores?.map(store => store.storeId) || [];
              onChange?.(allStoreIds);
            }}
          >
            全選択
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              onChange?.([]);
            }}
          >
            全解除
          </Button>
        </div>
        <Select
          mode="multiple"
          placeholder="送信する公式アカウントを選択してください"
          value={value}
          onChange={onChange}
        >
          {loginAccount.stores?.map((_store) => (
            <Select.Option value={_store.storeId} key={_store.storeId}>
              {_store.storeName}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  /**
   * 配信対象者詳細CSVダウンロード処理
   */
  const handleDownloadSegmentResult = useCallback(async (format: 'csv' | 'xlsx') => {
    if (!segmentResultData?.id) {
      message.error('セグメント検索結果が見つかりません');
      return;
    }

    try {
      setDownloadLoading(true);

      // repositoryから全配信対象を取得
      const deliveryTargets = await segmentResultRepo.getAllDeliveryTargets({
        account: loginAccount,
        segmentResultId: segmentResultData.id
      });

      if (deliveryTargets.length === 0) {
        message.warning('配信対象者が見つかりませんでした');
        return;
      }

      // CSVデータ作成
      const headers = ['LINE_ID', 'STORE_ID'];
      const rows = deliveryTargets.map(target => [
        target.lineId,
        target.storeId || '' // storeIdがnullの場合は空文字
      ]);

      const title = init?.broadCast?.title || 'セグメント配信';

      // ダウンロード実行
      download(
        [headers, ...rows],
        `${title}_配信対象者リスト`,
        format
      );

      message.success(`${deliveryTargets.length}件の配信対象者をダウンロードしました`);
    } catch (error) {
      message.error('ダウンロードに失敗しました');
    } finally {
      setDownloadLoading(false);
    }
  }, [segmentResultData, segmentResultRepo, loginAccount, init?.broadCast?.title]);

  /**
   * 配信タイプ変更時のフォームリセット処理
   * 新規作成時のみフォームをリセットする
   */
  React.useEffect(() => {
    if (deliveryTypeChanged && !init?.broadCast) {
      form.resetFields();
    }
  }, [deliveryTypeChanged, form, init?.broadCast]);

  return (
    <FullscreenLoading isLoading={downloadLoading}>
      <Form
        labelWrap
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16, offset: 1 }}
        layout="horizontal"
        style={{ maxWidth: 900, margin: 'auto' }}
        form={form}
        initialValues={initialValues}
        disabled={loading}
      >
        {/* テンプレート選択 */}
        <TemplateSelect onTemplateSelect={handleSelectTemplate} />

        {/* 基本情報 */}
        <Form.Item<BroadCastFormType>
          label="タイトル"
          name="title"
          rules={[{ required: true, message: 'タイトルを入力してください' }]}
          tooltip="こちらはお客さまに公開されません"
        >
          <Input />
        </Form.Item>

        {/* 全選択・全解除ボタン */}
        <Form.Item
          name="storeIds"
          label="送信するアカウント"
          rules={[
            {
              message: '送信する公式アカウントを選択してください',
              type: 'array',
            },
          ]}
        >
          <StoreSelect />
        </Form.Item>

        <Form.Item
          name="sendAt"
          label="送信日時"
          getValueProps={(i) => ({
            value: !!!i ? undefined : dayjs(i),
          })}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            disabledDate={(d) => d.diff(dayjs(), 'seconds') < 0}
          />
        </Form.Item>

        {/* 送信内容設定 */}
        <Form.Item
          name="type"
          label="送信内容タイプ"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { value: 'text', label: 'テキストのみ' },
              { value: 'image', label: '画像のみ' },
              { value: 'template', label: 'アクション付き画像のみ（クーポンなど）' },
              { value: 'textAndImage', label: 'テキスト＆画像' },
              { value: 'textAndTemplate', label: 'テキスト＆アクション付き画像' },
            ]}
          />
        </Form.Item>

        {/* テキスト入力（条件付き表示） */}
        <Form.Item
          hidden={!_needText}
          name="text"
          label="テキスト"
          required={_needText}
        >
          <TextArea minLength={1} maxLength={300} />
        </Form.Item>

        {/* 画像アップロード（条件付き表示） */}
        <Form.Item
          name="image"
          hidden={!(_needImage || _needTemplate)}
          label="画像"
          required={_needImage || _needTemplate}
        >
          <ImageUpload
            inputImage={inputImage}
            templateImageUrl={templateImageUrl}
            existingImageUrl={getExistingImageUrl()}
            onImageChange={handleImageChange}
            onTemplateImageClear={handleTemplateImageClear}
          />
        </Form.Item>

        {/* テンプレート用項目（条件付き表示） */}
        <Form.Item
          hidden={!_needTemplate}
          name="tempLabel"
          label="画像上のテキスト"
          required={_needTemplate}
        >
          <Input
            type="text"
            placeholder="クーポンをみる"
            minLength={1}
            maxLength={12}
          />
        </Form.Item>

        <Form.Item
          hidden={!_needTemplate}
          name="altText"
          label="通知に表示されるテキスト"
          required={_needTemplate}
        >
          <Input type="text" maxLength={400} />
        </Form.Item>

        <Form.Item
          hidden={!_needTemplate}
          name="tempUri"
          label="リンク先"
          required={_needTemplate}
        >
          <Input type="url" placeholder="http://mysite.com" maxLength={1000} />
        </Form.Item>

        <Divider />

        {/* セグメント条件設定 */}
        <Title level={5}>セグメント条件設定</Title>

        {/* セグメント条件選択タグ */}
        <Form.Item label="条件を追加">
          <Space wrap>
            {availableConditions.map(condition => (
              <Tag
                key={condition.key}
                icon={<PlusOutlined />}
                color={activeConditions.includes(condition.key) ? condition.color : 'default'}
                style={{ cursor: 'pointer' }}
                onClick={() => activeConditions.includes(condition.key)
                  ? removeCondition(condition.key)
                  : addCondition(condition.key)
                }
              >
                {condition.label}
              </Tag>
            ))}
          </Space>
        </Form.Item>

        {/* 特定日来店の条件フォーム */}
        {activeConditions.includes('specificDateVisit') && (
          <Card size="small" style={{ marginBottom: 16 }} title="特定日来店">
            <Form.Item
              name={['segmentConditions', 'specificDateVisit', 'targetDates']}
              label="対象日付"
              tooltip="選択した日付に来店した顧客を対象にします（複数日選択可能）"
            >
              <DatePicker
                // @ts-ignore - multiple is supported but type definition is not updated
                multiple
                placeholder="日付を選択（複数選択可能）"
                style={{ width: '100%' }}
                disabledDate={(current) => {
                  return current && current > dayjs().endOf('day');
                }}
              />
            </Form.Item>
          </Card>
        )}

        {/* 選択された条件のフォーム表示 */}
        {activeConditions.includes('visitPattern') && (
          <Card size="small" style={{ marginBottom: 16 }} title="来店パターン">
            <Form.Item
              name={['segmentConditions', 'visitPattern', 'dateRange']}
              label="来店期間"
              tooltip="指定期間内での来店を条件にします"
            >
              <RangePicker
                placeholder={['開始日', '終了日']}
                presets={[
                  { label: '今週', value: getPresetDateRange('thisWeek') },
                  { label: '先週', value: getPresetDateRange('lastWeek') },
                  { label: '今月', value: getPresetDateRange('thisMonth') },
                  { label: '先月', value: getPresetDateRange('lastMonth') },
                  { label: '半年前まで', value: getPresetDateRange('halfYear') },
                  { label: '今年中', value: getPresetDateRange('thisYear') },
                  { label: '去年中', value: getPresetDateRange('lastYear') },
                ]}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['segmentConditions', 'visitPattern', 'visitCount', 'count']}
                  label="来店回数"
                  initialValue={1}
                >
                  <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['segmentConditions', 'visitPattern', 'visitCount', 'operator']}
                  label="条件"
                  initialValue="above"
                >
                  <Select style={{ width: '100%' }}>
                    <Select.Option value="above">以上</Select.Option>
                    <Select.Option value="below">以下</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {activeConditions.includes('customerRank') && (
          <Card size="small" style={{ marginBottom: 16 }} title="顧客ランク">
            <Form.Item
              name={['segmentConditions', 'customerRank']}
              label="対象ランク"
            >
              <Checkbox.Group>
                <Checkbox value="1軍">1軍</Checkbox>
                <Checkbox value="2軍">2軍</Checkbox>
                <Checkbox value="3軍">3軍</Checkbox>
                <Checkbox value="スタッフ">スタッフ</Checkbox>
              </Checkbox.Group>
            </Form.Item>
          </Card>
        )}

        {activeConditions.includes('itemPreferences') && (
          <Card size="small" style={{ marginBottom: 16 }} title="商品購入パターン">
            <Form.Item
              name={['segmentConditions', 'itemPreferences', 'targetItemIds']}
              label="対象商品"
              tooltip="グランドメニューの商品を対象に設定します"
            >
              <Select mode="multiple" placeholder="商品を選択してください">
                {/* TODO: 商品マスタから取得 */}
              </Select>
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['segmentConditions', 'itemPreferences', 'purchaseCount', 'count']}
                  label="購入回数"
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['segmentConditions', 'itemPreferences', 'purchaseCount', 'operator']}
                  label="条件"
                >
                  <Select style={{ width: '100%' }}>
                    <Select.Option value="above">以上</Select.Option>
                    <Select.Option value="below">以下</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {activeConditions.includes('pointCard') && (
          <Card size="small" style={{ marginBottom: 16 }} title="ポイントカード">
            <Form.Item
              name={['segmentConditions', 'pointCard', 'cardIds']}
              label="カード種類"
            >
              <Select
                mode="multiple"
                placeholder="ポイントカードを選択"
                loading={pointCardLoading}
              >
                {pointCardData?.pointCards?.map((pointCard) => (
                  <Select.Option value={pointCard.id} key={pointCard.id}>
                    {pointCard.title}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['segmentConditions', 'pointCard', 'pointRange', 'min']}
                  label="ポイント数（最小）"
                  initialValue={0}
                >
                  <InputNumber min={0} max={999999} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['segmentConditions', 'pointCard', 'pointRange', 'max']}
                  label="ポイント数（最大）"
                  initialValue={999999}
                >
                  <InputNumber min={0} max={999999} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {activeConditions.includes('surveyAnswers') && (
          <Card size="small" style={{ marginBottom: 16 }} title="アンケート回答">
            <Form.List name={['segmentConditions', 'surveyAnswers']}>
              {(fields, { add, remove }) => {
                return (
                  <>
                    {fields.map((field, index) => {
                      const currentAnswer = surveyAnswers[field.name] || {};
                      const surveyId = currentAnswer.surveyId;
                      const selectedSurvey = surveyData?.surveys?.find(s => s.id === surveyId);
                      const questionId = currentAnswer.questionId;
                      const selectedQuestion = selectedSurvey?.questions?.find(q => q.id === questionId);

                      return (
                        <Card key={field.key} size="small" style={{ marginBottom: 12 }}
                          title={`アンケート条件 ${index + 1}`}
                          extra={fields.length > 1 ? (
                            <Button size="small" type="text" danger onClick={() => remove(field.name)}>
                              削除
                            </Button>
                          ) : null}>

                          {/* アンケート選択 */}
                          <Form.Item
                            name={[field.name, 'surveyId']}
                            label="アンケート"
                            rules={[{ required: true, message: 'アンケートを選択してください' }]}
                          >
                            <Select
                              placeholder="アンケートを選択"
                              loading={surveyLoading}
                              onChange={() => {
                                form.setFieldValue(['segmentConditions', 'surveyAnswers', field.name, 'questionId'], undefined);
                                form.setFieldValue(['segmentConditions', 'surveyAnswers', field.name, 'answerCondition'], undefined);
                              }}
                            >
                              {surveyData?.surveys?.map((survey) => (
                                <Select.Option value={survey.id} key={survey.id}>
                                  {survey.title}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          {/* 質問選択 */}
                          <Form.Item
                            name={[field.name, 'questionId']}
                            label="質問"
                            rules={[{ required: true, message: '質問を選択してください' }]}
                          >
                            <Select
                              placeholder="質問を選択"
                              disabled={!selectedSurvey}
                              onChange={(questionId) => {
                                // 選択された質問を取得
                                const selected = selectedSurvey?.questions?.find(q => q.id === questionId);
                                if (selected) {
                                  form.setFieldValue(
                                    ['segmentConditions', 'surveyAnswers', field.name, 'answerCondition'],
                                    { type: selected.type }
                                  );
                                }
                              }}
                            >
                              {selectedSurvey?.questions?.map((question) => (
                                <Select.Option value={question.id} key={question.id}>
                                  {question.question}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          {/* 回答条件設定 */}
                          {selectedQuestion && (
                            <Form.Item label="回答条件">
                              {selectedQuestion.type === 'text' && (
                                <Form.Item
                                  name={[field.name, 'answerCondition', 'textContains']}
                                  label="含む文字列"
                                >
                                  <Input placeholder="回答に含まれる文字列を入力" />
                                </Form.Item>
                              )}

                              {selectedQuestion.type === 'number' && (
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item
                                      name={[field.name, 'answerCondition', 'numberRange', 'min']}
                                      label="最小値"
                                    >
                                      <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      name={[field.name, 'answerCondition', 'numberRange', 'max']}
                                      label="最大値"
                                    >
                                      <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              )}

                              {(selectedQuestion.type === 'radio' || selectedQuestion.type === 'checkbox') && (
                                <Form.Item
                                  name={[field.name, 'answerCondition', 'selectedOptions']}
                                  label="選択された選択肢"
                                >
                                  <Select mode="multiple" placeholder="対象の選択肢を選択">
                                    {selectedQuestion.selections?.map((selection, idx) => (
                                      <Select.Option value={selection} key={idx}>
                                        {selection}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              )}

                              <Form.Item
                                name={[field.name, 'answerCondition', 'type']}
                                hidden
                                initialValue={selectedQuestion.type}
                              >
                                <Input />
                              </Form.Item>
                            </Form.Item>
                          )}
                        </Card>
                      );
                    })}

                    <Button type="dashed" onClick={() => add()} block>
                      + アンケート条件を追加
                    </Button>
                  </>
                );
              }}
            </Form.List>
          </Card>
        )}

        <Divider />

        {/* 送信店舗選択設定 */}
        <Title level={5}>送信店舗選択設定</Title>

        <Form.Item
          name="storeSelectionStrategy"
          label="送信店舗選択ルール"
          rules={[{ required: true, message: '店舗選択ルールを選択してください' }]}
          tooltip="複数店舗が該当する場合の優先順位を設定します。最終的に最終来店店舗で必ず1店舗に決定されます。"
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="recent_only">
                <strong>最終来店店舗</strong>のみで決定
              </Radio>
              <Radio value="most_visits_then_recent">
                <strong>来店回数最多店舗</strong> → 最終来店店舗
              </Radio>
              <Radio value="most_visits_retchu_recent">
                <strong>来店回数最多店舗</strong> → れっちゅー加入店舗 → 最終来店店舗
              </Radio>
              <Radio value="retchu_then_recent">
                <strong>れっちゅー加入店舗</strong> → 最終来店店舗
              </Radio>
              <Radio value="retchu_most_visits_recent">
                <strong>れっちゅー加入店舗</strong> → 来店回数最多店舗 → 最終来店店舗
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Divider />

        {/* セグメント検索結果プレビュー */}
        {(init?.broadCast?.segmentJobStatus === 'completed' ||
          init?.broadCast?.segmentJobStatus === 'error') && (
            <SegmentResultPreview />
          )}

        {/* セグメント検索・配信ボタン */}
        <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: 'center' }}>
          <Space size="large">
            {/* 下書き保存 */}
            <Button
              disabled={
                init?.broadCast?.status === 'sent' ||
                init?.broadCast?.segmentJobStatus === 'processing'
              }
              loading={loading}
              type="dashed"
              onClick={onSaveDraft}
            >
              下書き保存
            </Button>

            {/* 配信先検索ボタン */}
            <Button
              type="primary"
              loading={searchLoading}
              onClick={onSegmentSearch}
              disabled={
                activeConditions.length === 0 ||
                init?.broadCast?.segmentJobStatus === 'processing' ||
                init?.broadCast?.status === 'sent'
              }
            >
              {init?.broadCast?.segmentJobStatus === 'processing' ? '検索中...' : '配信先を検索'}
            </Button>

            {/* 配信確定（検索結果がある場合のみ） */}
            <Button
              disabled={
                init?.broadCast?.status === 'sent' ||
                init?.broadCast?.segmentJobStatus !== 'completed' ||
                !segmentResultData?.customerCount
              }
              loading={loading}
              type="primary"
              danger
              onClick={onConfirmDelivery}
            >
              配信確定
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </FullscreenLoading>
  );
};
